import {
  type AskClarifyingQuestionsOutput,
  CLARIFY_LIMITS,
  type ClarifyingQuestion,
} from '@realadvisor/common-types';

import type { ToolPart } from './ApprovalActions';

// Pure helpers for the `ask_clarifying_questions` card (ClarifyingQuestionsCard).

export type ClarifyResolveHandler = (
  toolCallId: string,
  output: AskClarifyingQuestionsOutput,
) => void;

export type QuestionAnswer = {
  // Selected predefined options; single choice keeps at most one.
  selected: Array<string>;
  otherSelected: boolean;
  otherText: string;
  // Free text for `type: 'text'` questions.
  text: string;
};

export type AnswerMap = Record<string, QuestionAnswer>;

export const emptyAnswer = (): QuestionAnswer => ({
  selected: [],
  otherSelected: false,
  otherText: '',
  text: '',
});

// The card appends its own free-text "Other…" row; a model-written one would
// show twice.
const OTHER_LIKE_OPTION =
  /^(other|something else|autre|andere|altro|otro)[\s\W]*$/i;

export const isOtherLikeOption = (option: string): boolean =>
  OTHER_LIKE_OPTION.test(option.trim());

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

// Tool input is raw model output (and may still be streaming): keep only what
// the card can render safely, and never trust the shape. The server validates
// the same limits; capping here keeps a submitted answer within them.
export const sanitizeQuestions = (
  input: unknown,
): Array<ClarifyingQuestion> => {
  if (!isRecord(input) || !Array.isArray(input.questions)) {
    return [];
  }
  const seenIds = new Set<string>();
  const questions: Array<ClarifyingQuestion> = [];
  for (const raw of input.questions) {
    if (
      !isRecord(raw) ||
      !isNonEmptyString(raw.id) ||
      !isNonEmptyString(raw.prompt) ||
      seenIds.has(raw.id)
    ) {
      continue;
    }
    seenIds.add(raw.id);
    const rawOptions = Array.isArray(raw.options)
      ? raw.options.filter(isNonEmptyString).map(option => option.trim())
      : [];
    const hadOtherLike = rawOptions.some(isOtherLikeOption);
    const options = [
      ...new Set(rawOptions.filter(option => !isOtherLikeOption(option))),
    ].slice(0, CLARIFY_LIMITS.maxOptions);
    const prompt = raw.prompt.trim();
    // A choice question left without options can still be answered in text.
    if (raw.type === 'text' || options.length === 0) {
      questions.push({ id: raw.id, prompt, type: 'text' });
    } else {
      const allowMultiple = raw.allowMultiple === true;
      const recommendedOption =
        !allowMultiple &&
        typeof raw.recommendedOption === 'string' &&
        options.includes(raw.recommendedOption.trim())
          ? raw.recommendedOption.trim()
          : undefined;
      questions.push({
        id: raw.id,
        prompt,
        type: 'choice',
        options,
        allowMultiple,
        allowOther: raw.allowOther !== false || hadOtherLike,
        ...(recommendedOption != null ? { recommendedOption } : {}),
      });
    }
    if (questions.length === CLARIFY_LIMITS.maxQuestions) {
      break;
    }
  }
  return questions;
};

const capAnswer = (value: string): string =>
  value.trim().slice(0, CLARIFY_LIMITS.maxAnswerLength);

export const buildResponse = (
  question: ClarifyingQuestion,
  answer: QuestionAnswer,
): string | Array<string> => {
  if (question.type === 'text') {
    return capAnswer(answer.text);
  }
  const options = question.options ?? [];
  const values = options.filter(option => answer.selected.includes(option));
  if (answer.otherSelected && answer.otherText.trim().length > 0) {
    values.push(capAnswer(answer.otherText));
  }
  return question.allowMultiple === true ? values : values[0] ?? '';
};

export const hasAnswer = (
  question: ClarifyingQuestion,
  answer: QuestionAnswer,
): boolean => {
  const response = buildResponse(question, answer);
  return response.length > 0;
};

export const buildClarifyOutput = (
  questions: ReadonlyArray<ClarifyingQuestion>,
  answers: AnswerMap,
): AskClarifyingQuestionsOutput => ({
  success: true,
  answers: questions.map(question => ({
    id: question.id,
    prompt: question.prompt,
    response: buildResponse(question, answers[question.id] ?? emptyAnswer()),
  })),
});

export const skippedOutput = (): AskClarifyingQuestionsOutput => ({
  success: true,
  skipped: true,
});

export const invalidInputOutput = (): AskClarifyingQuestionsOutput => ({
  success: false,
  error: 'No valid question in the tool input',
});

// `pending`: the last turn is still streaming (input may be partial).
// `error`: a failed or unrecognised output, shown verbatim.
export type ClarifyCardMode =
  | 'interactive'
  | 'pending'
  | 'answered'
  | 'skipped'
  | 'notAnswered'
  | 'error';

export const clarifyCardMode = ({
  part,
  isLastAssistant,
  isLoading,
}: {
  part: ToolPart;
  isLastAssistant: boolean;
  isLoading: boolean;
}): ClarifyCardMode => {
  if (part.state === 'output-error' || part.errorText != null) {
    return 'error';
  }
  if (part.state === 'output-available' || part.output != null) {
    const output = part.output;
    if (!isRecord(output) || output.success !== true) {
      return 'error';
    }
    if (output.notAnswered === true) {
      return 'notAnswered';
    }
    if (output.skipped === true) {
      return 'skipped';
    }
    return Array.isArray(output.answers) ? 'answered' : 'error';
  }
  if (!isLastAssistant) {
    // A newer message was sent while the card was open (or the stream died).
    return 'notAnswered';
  }
  if (isLoading) {
    return 'pending';
  }
  return part.state === 'input-available' ? 'interactive' : 'notAnswered';
};

export const clarifyErrorText = (part: ToolPart): string => {
  if (part.errorText != null) {
    return part.errorText;
  }
  const output = part.output;
  if (isRecord(output) && typeof output.error === 'string') {
    return output.error;
  }
  try {
    return JSON.stringify(output, null, 2) ?? String(output);
  } catch {
    return String(output);
  }
};

export type ClarifySummaryRow = {
  id: string;
  prompt: string;
  // null when the question got no answer (skipped, not answered, left empty).
  response: string | null;
};

const formatResponse = (response: unknown): string | null => {
  if (Array.isArray(response)) {
    const values = response.filter(isNonEmptyString);
    return values.length > 0 ? values.join(', ') : null;
  }
  return isNonEmptyString(response) ? response : null;
};

// Answered cards list the saved answers (they carry their prompt), falling back
// to the input's questions so every asked question gets a row.
export const clarifySummaryRows = (
  part: ToolPart,
): Array<ClarifySummaryRow> => {
  const questions = sanitizeQuestions(part.input);
  const output = isRecord(part.output) ? part.output : {};
  const answers = Array.isArray(output.answers)
    ? output.answers.filter(isRecord)
    : [];
  const responseOf = (id: string): unknown =>
    answers.find(answer => answer.id === id)?.response;
  const rows: Array<ClarifySummaryRow> = questions.map(question => ({
    id: question.id,
    prompt: question.prompt,
    response: formatResponse(responseOf(question.id)),
  }));
  const known = new Set(questions.map(question => question.id));
  for (const answer of answers) {
    if (
      isNonEmptyString(answer.id) &&
      isNonEmptyString(answer.prompt) &&
      !known.has(answer.id)
    ) {
      known.add(answer.id);
      rows.push({
        id: answer.id,
        prompt: answer.prompt,
        response: formatResponse(answer.response),
      });
    }
  }
  return rows;
};
